import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { RequestServiceBase } from './request.service.base';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { differenceInSeconds } from 'date-fns';
import { AuthenticationService } from '../authentication/authentication.service';

@Injectable({ providedIn: 'root' })
export class HttpGatewayService extends RequestServiceBase {
    private requestsCache: { [key: string]: { result: Observable<any>; timestamp: Date } } = {};
    public cacheExpirationInSeconds = 60 * 3;

    private http = inject(HttpClient);
    private authenticationService = inject(AuthenticationService);

    public get<T>(url: string, useCaching = false): Observable<T> {
        return this.createHeaders().pipe(
            switchMap((headers) => {
                if (useCaching) {
                    const key = btoa(url);
                    if (
                        !this.requestsCache.hasOwnProperty(key) ||
                        this.isExpired(key) ||
                        (this.requestsCache.hasOwnProperty(key) && this.requestsCache[key].result === null)
                    ) {
                        const observable = this.http
                            .get(this.resolveUrl(url), {
                                headers,
                                withCredentials: true,
                            })
                            .pipe(
                                shareReplay(),
                                map(this.checkResponseBody.bind(this)),
                                catchError(this.handleError.bind(this)),
                            );
                        const value = { result: observable, timestamp: new Date() };
                        this.requestsCache[key] = value;
                        return observable;
                    } else {
                        return this.requestsCache[key].result;
                    }
                } else {
                    return this.http
                        .get(this.resolveUrl(url), {
                            headers,
                            withCredentials: true,
                        })
                        .pipe(map(this.checkResponseBody.bind(this)), catchError(this.handleError.bind(this)));
                }
            }),
        );
    }

    public delete(url: string): Observable<any> {
        return this.createHeaders().pipe(
            switchMap((headers) =>
                this.http
                    .delete(this.resolveUrl(url), {
                        headers,
                        withCredentials: true,
                    })
                    .pipe(map(this.checkResponseBody.bind(this)), catchError(this.handleError.bind(this))),
            ),
        );
    }

    public post<T>(
        url: string,
        body: any,
        useCaching = false,
        headersToUse?: HttpHeaders,
    ): Observable<T> {
        return this.createHeaders(headersToUse).pipe(
            switchMap((headers) => {
                if (useCaching) {
                    const key = btoa(JSON.stringify({ url, body }));
                    if (
                        !this.requestsCache.hasOwnProperty(key) ||
                        this.isExpired(key) ||
                        (this.requestsCache.hasOwnProperty(key) && this.requestsCache[key].result === null)
                    ) {
                        const observable = this.http
                            .post(this.resolveUrl(url), body, {
                                headers,
                                withCredentials: true,
                            })
                            .pipe(
                                shareReplay(),
                                map(this.checkResponseBody.bind(this)),
                                catchError(this.handleError.bind(this)),
                            );
                        const value = { result: observable, timestamp: new Date() };
                        this.requestsCache[key] = value;
                        return observable;
                    } else {
                        return this.requestsCache[key].result;
                    }
                } else {
                    return this.http
                        .post(this.resolveUrl(url), body, {
                            headers,
                            withCredentials: true,
                        })
                        .pipe(map(this.checkResponseBody.bind(this)), catchError(this.handleError.bind(this)));
                }
            }),
        );
    }

    public put<T>(url: string, body: any): Observable<T> {
        return this.createHeaders().pipe(
            switchMap((headers) =>
                this.http
                    .put(this.resolveUrl(url), body, {
                        headers,
                        withCredentials: true,
                    })
                    .pipe(map(this.checkResponseBody.bind(this)), catchError(this.handleError.bind(this))),
            ),
        );
    }

    public downloadFileWithPostRequest(url: string, body: any): Observable<Blob> {
        return this.createHeaders().pipe(
            switchMap((headers) =>
                this.http
                    .post(this.resolveUrl(url), body, {
                        headers,
                        responseType: 'blob',
                        withCredentials: true,
                    })
                    .pipe(map(this.checkResponseBody.bind(this)), catchError(this.handleError.bind(this))),
            ),
        );
    }

    public downloadFileWithGetRequest(url: string): Observable<Blob> {
        return this.createHeaders().pipe(
            switchMap((headers) =>
                this.http
                    .get(this.resolveUrl(url), {
                        headers,
                        responseType: 'blob',
                        withCredentials: true,
                    })
                    .pipe(map(this.checkResponseBody.bind(this)), catchError(this.handleError.bind(this))),
            ),
        );
    }

    public postStream<T>(url: string, body: any): Observable<T> {
        return this.authenticationService.tryRefreshSecurityToken().pipe(
            switchMap((token) => {
                return new Observable<T>((subscriber) => {
                    const abortController = new AbortController();

                    const headers: Record<string, string> = {
                        'Content-Type': 'application/json',
                    };
                    if (token) {
                        headers['Authorization'] = `Bearer ${token}`;
                    }

                    fetch(this.resolveUrl(url), {
                        method: 'POST',
                        headers,
                        body: JSON.stringify(body),
                        signal: abortController.signal,
                    })
                        .then(async (response) => {
                            if (!response.ok) {
                                throw new Error(`HTTP error! status: ${response.status}`);
                            }

                            const reader = response.body?.getReader();
                            if (!reader) {
                                throw new Error('No response body');
                            }

                            const decoder = new TextDecoder();
                            let buffer = '';

                            while (true) {
                                const { done, value } = await reader.read();

                                if (done) {
                                    subscriber.complete();
                                    break;
                                }

                                buffer += decoder.decode(value, { stream: true });
                                const lines = buffer.split('\n');
                                buffer = lines.pop() || '';

                                for (const line of lines) {
                                    if (line.trim()) {
                                        try {
                                            const message = JSON.parse(line) as T;
                                            subscriber.next(message);
                                        } catch (e) {
                                            console.warn('Failed to parse stream message:', line);
                                        }
                                    }
                                }
                            }
                        })
                        .catch((error) => {
                            if (error.name !== 'AbortError') {
                                subscriber.error(error);
                            }
                        });

                    return () => {
                        abortController.abort();
                    };
                });
            }),
        );
    }

    private createHeaders(headersToUse?: HttpHeaders): Observable<HttpHeaders> {
        return this.authenticationService.tryRefreshSecurityToken().pipe(
            map((token) => {
                let headers: HttpHeaders;
                if (headersToUse) {
                    headers = headersToUse;
                } else {
                    headers = new HttpHeaders({
                        'Content-Type': 'application/json',
                    });
                }
                if (token) {
                    headers = headers.set('Authorization', 'Bearer ' + token);
                }
                return headers;
            }),
        );
    }

    private isExpired(key: string) {
        return (
            this.requestsCache.hasOwnProperty(key) &&
            differenceInSeconds(new Date(), this.requestsCache[key].timestamp) > this.cacheExpirationInSeconds
        );
    }
}
